import type { FrameworkId } from "@/modules/lernpfad/data/frameworks"
import type { Persona } from "@/app/demo/lernpfad/use-lernpfad-demo"

/**
 * The guided path through the demo.
 *
 * The five views are a product surface, not a story — a viewer who opens the
 * public link alone gets a tab bar and no idea that the framework switch is
 * the point, or that there is a learning portal behind the Integrationen
 * tile. This turns them into one arc: an individual, their manager, the whole
 * organisation, the model underneath, and finally what the employee sees.
 *
 * Two steps APPLY state rather than describing it. A tour that says "try
 * dragging the Pensum slider" is one most people read and skip; one that moves
 * the slider for you shows the recomputation happening, which is the entire
 * claim. Each step declares the full state it wants rather than a delta, so
 * re-entering the tour mid-way lands somewhere coherent.
 *
 * `apply` takes only the three actions a step may use, NOT the whole demo
 * state: importing `LernpfadDemoState` here would point this module back at
 * the hook that renders the tour, and handing a step the entire state invites
 * exactly the stale-closure bug that a narrow, stable-callback contract makes
 * impossible.
 *
 * The LMS is deliberately NOT auto-opened on the last step: it is a Dialog,
 * and its overlay would cover the tour bar that is supposed to end the tour.
 */

export interface TourActions {
    setPensumOverride: (value: number | null) => void
    resetPolicy: () => void
    toggleFramework: (id: FrameworkId) => void
}

export interface TourStep {
    id: string
    persona: Persona
    /** State this step wants in place. Applied when the step becomes active. */
    apply?: (actions: TourActions) => void
}

export const TOUR_STEPS: TourStep[] = [
    {
        id: "plan",
        persona: "mitarbeitende",
        apply: ({ setPensumOverride, resetPolicy }) => {
            setPensumOverride(null)
            resetPolicy()
        },
    },
    {
        // The claim is "a lever changes the engine input, not a filter over a
        // precomputed list" — so move one and let the plan re-budget.
        id: "levers",
        persona: "mitarbeitende",
        apply: ({ setPensumOverride }) => setPensumOverride(60),
    },
    {
        id: "manager",
        persona: "fuehrung",
        apply: ({ setPensumOverride }) => setPensumOverride(null),
    },
    {
        id: "hr",
        persona: "hr",
    },
    {
        // The sharpest moment in the demo: the same completions, judged by a
        // stricter standard, stop clearing the bar.
        id: "model",
        persona: "steuerung",
        apply: ({ resetPolicy, toggleFramework }) => {
            resetPolicy()
            toggleFramework("ltem")
        },
    },
    {
        id: "lms",
        persona: "integrationen",
    },
]
